import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import { normalizeValue } from '../../../../../utils/Texts';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './MenuModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Menús del Sistema";
const endpoint = ENDPOINTS.menus;
const endpointById = ENDPOINTS.menusById;
const modelo = Models.menu;

export default function MenusCatalog() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });
    const [editingRowId, setEditingRowId] = useState(null);

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    // Auto-scroll al formulario cuando se abre
    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(endpoint);
            setRows((res.data || []).map((x) => modelo(x)));
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar información');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const modulo = (r.modulo || '').toLowerCase();
            const nombre = (r.nombre || '').toLowerCase();
            const clave = (r.clave || '').toLowerCase();
            const ruta = (r.ruta || '').toLowerCase();
            return (
                modulo.includes(q) || nombre.includes(q) || clave.includes(q) || ruta.includes(q)
            );
        });
    }, [rows, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);
    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);


    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
        setEditingRowId(null);
    };

    const handleEdit = (id) => {
        const r = rows.find(x => x.idMenu === id);
        if (!r) return;
        setSelectedId(id);
        setEditData(r);
        setModalMode('edit');
        setModalOpen(true);
        setEditingRowId(id);
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (payload, mode) => {
        try {
            setLoading(true);
            const body = {
                // El API está configurado con PropertyNamingPolicy = null, por lo que espera PASCAL CASE
                IdMenu: payload.idMenu ?? 0,
                IdModulo: Number(payload.idModulo),
                Modulo: payload.modulo ?? "",
                Nombre: payload.nombre ?? "",
                Clave: payload.clave ?? "",
                Nivel: parseInt(payload.nivel || 1, 10),
                Orden: parseInt(payload.orden || 0, 10),
                Ruta: payload.ruta ?? null,
                Icono: payload.icono ?? "",
                EsRaizModulo: !!payload.esRaizModulo,
                Activo: (payload.activo === undefined) ? true : !!payload.activo,
                FechaCreacion: payload.fechaCreacion || new Date().toISOString(),
                IdPadre: (payload.idPadre && Number(payload.idPadre) !== 0) ? Number(payload.idPadre) : null,
                IdRecurso: (payload.idRecurso && Number(payload.idRecurso) !== 0) ? Number(payload.idRecurso) : null,
            };

            let newId = null;
            if (mode === 'new') {
                const res = await axios.post(endpoint, body);
                newId = res?.data?.idMenu ?? (typeof res?.data === 'number' ? res.data : null);
            } else {
                await axios.put(endpoint, body);
                newId = payload.idMenu;
            }

            if (newId) setEditingRowId(newId);
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error('Error al guardar menú:', e);
            const detail = e.response?.data?.errors 
                ? JSON.stringify(e.response.data.errors) 
                : (e.response?.data?.message || e.message);
            triggerAlert(`Error al guardar datos. Detalles: ${detail}`);
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >{label}</button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>
                    <input
                        className="roles-search"
                        placeholder="Buscar por módulo, nombre, ruta..."
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Módulo</th>
                                    <th>Nombre</th>
                                    <th>Clave</th>
                                    <th>Ruta</th>
                                    <th>Nivel</th>
                                    <th>Orden</th>
                                    <th>Activo</th>
                                    <th style={{ textAlign: 'center' }}>Editar</th>
                                    <th style={{ textAlign: 'center' }}>Borrar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idMenu} className={editingRowId === r.idMenu ? 'rk-row-active' : ''}>
                                        <td>{normalizeValue(r.modulo)}</td>
                                        <td>{normalizeValue(r.nombre)}</td>
                                        <td><strong>{normalizeValue(r.clave)}</strong></td>
                                        <td>{normalizeValue(r.ruta)}</td>
                                        <td>{r.nivel}</td>
                                        <td>{r.order ?? r.orden}</td>
                                        <td>{r.activo ? 'Sí' : 'No'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button className="rk-iconbtn rk-iconbtn--edit" onClick={() => handleEdit(r.idMenu)}>
                                                <img src={sigmaEdit} alt="Editar" className="rk-ico" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button className="rk-iconbtn rk-iconbtn--del" onClick={() => askDelete(r.idMenu, r.nombre)}>
                                                <img src={sigmaDelete} alt="Borrar" className="rk-ico" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (<tr><td colSpan={8}>Sin resultados.</td></tr>)}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0 ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span> : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
                        {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
                        <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        <Modal
                            open={modalOpen}
                            mode={modalMode}
                            data={editData}
                            inline
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                        />
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
